import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Container,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import CustomSelect from "../../../components/Select/Select";
import TopBar from "../../../components/TopBar/TopBar";
import { transactionTypeList } from "../../../util/dropList";
import AdjustmentHistory from "./AdjustmentHistory";
import Main from "./Dashboard.js/Main";
import InventoryDashboard from "./InventoryDashboard";
import InventorySettingsCategory from "./InventorySettingsCategory";
import Location from "./Location";
import LocationCategory from "./LocationCategory";
import PendingReview from "./PendingReview";
import PurchaseInvoice from "./PurchaseInvoice";
import PurchaseOrders from "./PurchaseOrders";
import Service from "./Service";
import Stock from "./Stock";
import Main2 from "./StockDashboard.js/Main2";
import StockPendingTable from "./StockPendingTable";
import StockRequest from "./StockRequest";
import Supplier from "./Supplier";
import TransactionHistory from "./TransactionHistory";

const subTabsToShow = ["Stock", "Purchase", "Adjustments & Transfers"];

const stockList = ["All Stock", "Low Stock", "Near Expiry", "Out of Stock"];
const purchaseList = ["Purchase Orders", "Purchase Invoice", "Pending to Add"];
const adjustmentsTransfersList = [
  "Transfers",
  "Adjustment History",
  "Pending Review",
];

export const INVENTORY_TAB_PATHS = {
  Dashboard: "dashboard",
  Stock: "stock",
  Service: "service",
  Purchase: "purchase",
  "Adjustments & Transfers": "adjustments-transfers",
  // legacy aliases
  "Request & Transfer": "adjustments-transfers",
  "Returns & Adjustments": "adjustments-transfers",
  Report: "report",
  Master: "master",
};

export const INVENTORY_PATH_TO_TAB = {
  dashboard: "Dashboard",
  stock: "Stock",
  service: "Service",
  purchase: "Purchase",
  "adjustments-transfers": "Adjustments & Transfers",
  // legacy aliases
  "request-transfer": "Adjustments & Transfers",
  "returns-adjustments": "Adjustments & Transfers",
  report: "Report",
  master: "Master",
};

export const INVENTORY_SUB_TAB_PATHS = {
  "All Stock": "all-stock",
  "Low Stock": "low-stock",
  "Near Expiry": "near-expiry",
  "Out of Stock": "out-of-stock",
  "Purchase Orders": "purchase-orders",
  "Purchase Invoice": "purchase-invoice",
  "Pending to Add": "pending-to-add",
  Transfers: "transfers",
  "Adjustment History": "adjustment-history",
  "Pending Review": "pending-review",
  Stock: "stock",
  Service: "service",
  Services: "services",
  Location: "location",
  Supplier: "supplier",
  "Inventory Settings": "inventory-settings",
};

export const INVENTORY_PATH_TO_SUB_TAB = {
  "all-stock": "All Stock",
  "low-stock": "Low Stock",
  "near-expiry": "Near Expiry",
  "out-of-stock": "Out of Stock",
  "purchase-orders": "Purchase Orders",
  "purchase-invoice": "Purchase Invoice",
  "pending-to-add": "Pending to Add",
  transfers: "Transfers",
  "adjustment-history": "Adjustment History",
  "pending-review": "Pending Review",
  stock: "Stock",
  service: "Service",
  services: "Services",
  location: "Location",
  supplier: "Supplier",
  "inventory-settings": "Inventory Settings",
};

export const InventoryRedirect = () => {
  const location = useLocation();
  const tabFromState = location.state?.activeTab ?? location.state?.selectedTab;
  const path =
    INVENTORY_TAB_PATHS[tabFromState] ?? INVENTORY_TAB_PATHS.Dashboard;
  const tabName = INVENTORY_PATH_TO_TAB[path];
  const defaultSub =
    tabName === "Report"
      ? "Stock"
      : tabName === "Master"
        ? "Location"
        : tabName === "Stock"
          ? "All Stock"
          : tabName === "Purchase"
            ? "Purchase Orders"
            : tabName === "Adjustments & Transfers"
              ? "Transfers"
              : null;
  const subPath = defaultSub ? INVENTORY_SUB_TAB_PATHS[defaultSub] : null;
  return (
    <Navigate
      to={subPath ? `/inventory/${path}/${subPath}` : `/inventory/${path}`}
      replace
      state={location.state}
    />
  );
};

const Inventory = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab, subTab: subTabParam } = useParams();
  const activeTab = INVENTORY_PATH_TO_TAB[tab];
  const fetchRef = useRef(false);
  const [penTblVsble, setPenTblVsble] = useState(false);
  const { pendingStocks } = useSelector((state) => state?.clinic);
  const [anchorEl, setAnchorEl] = useState(null);
  const [serviceOption, setServiceOption] = useState("Service");
  const [stockOption, setStockOption] = useState("Stock");
  const [serviceAnchorEl, setServiceAnchorEl] = useState(null);
  const [stockAnchorEl, setStockAnchorEl] = useState(null);
  const [masterAnchorEl, setMasterAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [reportAnchorEl, setReportAnchorEl] = useState(null);
  const [locationInnerTab, setLocationInnerTab] = useState("Location");
  const [inventorySettingsInnerTab, setInventorySettingsInnerTab] =
    useState("Stock");
  const [stockTransModValues, setStockTransModValues] = useState({
    visible: false,
  });
  const role = localStorage.getItem("role");
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { stocks } = useMemo(() => {
    if (!mandateDataRaw) {
      return { stocks: [] };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        stocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
      };
    } catch {
      return { stocks: [] };
    }
  }, [mandateDataRaw]);

  const allStocksList = useMemo(() => {
    const stockMap = new Map();
    (stocks ?? [])?.forEach((li) => {
      const stockId = li?.stockId ?? null;
      if (!stockId || stockMap.has(stockId)) return;
      const name = li?.tradeName ?? li?.productName ?? "";
      stockMap.set(stockId, {
        name,
        value: stockId,
        stockId,
        _searchText: name?.toLowerCase(),
      });
    });
    return Array.from(stockMap.values());
  }, [stocks]);

  const getTabBasedSubList = (tabName) => {
    if (!subTabsToShow.includes(tabName)) return null;
    if (tabName === "Stock") return stockList;
    if (tabName === "Purchase") return purchaseList;
    if (tabName === "Adjustments & Transfers") return adjustmentsTransfersList;
    return null;
  };

  const getDefaultSubTab = (tabName) => {
    if (tabName === "Report") return "Stock";
    if (tabName === "Master") return "Location";
    const subTabs = getTabBasedSubList(tabName);
    return subTabs?.length > 0 ? subTabs[0] : null;
  };

  // Redirect legacy tab paths to the merged Adjustments & Transfers route
  if (tab === "request-transfer" || tab === "returns-adjustments") {
    const legacySub =
      tab === "returns-adjustments"
        ? (INVENTORY_SUB_TAB_PATHS[
            INVENTORY_PATH_TO_SUB_TAB[subTabParam] ?? "Adjustment History"
          ] ?? INVENTORY_SUB_TAB_PATHS["Adjustment History"])
        : INVENTORY_SUB_TAB_PATHS.Transfers;
    return (
      <Navigate
        to={`/inventory/${INVENTORY_TAB_PATHS["Adjustments & Transfers"]}/${legacySub}`}
        replace
        state={location.state}
      />
    );
  }

  const activeSubTab =
    INVENTORY_PATH_TO_SUB_TAB[subTabParam] ??
    getDefaultSubTab(activeTab) ??
    null;

  const navigateToTab = (nextTab, nextSubTab = undefined) => {
    const path = INVENTORY_TAB_PATHS[nextTab];
    if (!path) {
      navigate(`/inventory/${INVENTORY_TAB_PATHS.Dashboard}`, {
        state: location.state,
      });
      return;
    }
    const resolvedSub =
      nextSubTab === undefined ? getDefaultSubTab(nextTab) : nextSubTab;
    const subPath = resolvedSub ? INVENTORY_SUB_TAB_PATHS[resolvedSub] : null;
    navigate(subPath ? `/inventory/${path}/${subPath}` : `/inventory/${path}`, {
      state: location.state,
    });
  };

  const setActiveTab = (nextTab, nextSubTab) => {
    if (!nextTab) return;
    navigateToTab(nextTab, nextSubTab);
  };

  const setActiveSubTab = (nextSubTab) => {
    if (nextSubTab === null || nextSubTab === undefined) {
      if (activeTab) navigateToTab(activeTab, null);
      return;
    }
    if (!activeTab) return;
    navigateToTab(activeTab, nextSubTab);
  };

  const handlePending = () => {
    setPenTblVsble(true);
    navigateToTab("Stock");
  };

  const handleChangeTab = (tabName) => {
    navigateToTab(tabName);
  };

  const onSubTabChange = (st) => {
    navigateToTab(activeTab, st);
  };

  const onOpenStockTransReport = () => {
    const today = new Date();
    setStockTransModValues({
      visible: true,
      startDate: new Date(today.getFullYear(), today.getMonth(), 1),
      endDate: today,
      stockName: null,
      type: "all",
    });
    setDownloadAnchorEl(null);
  };

  const onCloseStockTransMod = () => {
    setStockTransModValues({ visible: false });
  };

  const onChangeStockTrans = (name, value) => {
    const reqObj = { ...stockTransModValues, [name]: value };
    if (name === "stockName") {
      reqObj.stockNameErr = value?.value ? "" : "This field is required";
    }
    setStockTransModValues(reqObj);
  };

  const onGenerateStockTrans = () => {
    if (!stockTransModValues?.stockName?.value) {
      return setStockTransModValues({
        ...stockTransModValues,
        stockNameErr: "This field is required",
      });
    }
    setStockTransModValues({ ...stockTransModValues, valueEntered: true });
  };

  if (!activeTab) {
    return (
      <Navigate
        to={`/inventory/${INVENTORY_TAB_PATHS.Dashboard}`}
        replace
        state={location.state}
      />
    );
  }

  return (
    <>
      <TopBar>
        <Container maxWidth="xl">
          <div className="flex-row-ali-cen">
            <div
              className="ph10pv3"
              onClick={() => handleChangeTab("Dashboard")}
            >
              <div
                className={`flex-center cursor ${
                  activeTab === "Dashboard"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Dashboard
              </div>
            </div>
            <div className="ph10pv3" onClick={() => handleChangeTab("Stock")}>
              <div
                className={`flex-center cursor ${
                  activeTab === "Stock"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Stock
              </div>
            </div>
            <div className="ph10pv3" onClick={() => handleChangeTab("Service")}>
              <div
                className={`flex-center cursor ${
                  activeTab === "Service"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Service
              </div>
            </div>
            <div
              className="ph10pv3"
              onClick={() => handleChangeTab("Purchase")}
            >
              <div
                className={`flex-center cursor ${
                  activeTab === "Purchase"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Purchase
              </div>
            </div>
            <div
              className="ph10pv3"
              onClick={() => handleChangeTab("Adjustments & Transfers")}
            >
              <div
                className={`flex-center cursor ${
                  activeTab === "Adjustments & Transfers"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Adjustments & Transfers
              </div>
            </div>
            {/* <div>
              <div
                onMouseEnter={(e) => {
                  setReportAnchorEl(e.currentTarget);
                }}
                className={`flex-center cursor ${
                  activeTab === "Report"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Report <ArrowDropDownIcon fontSize="small" />
              </div>
              <Menu
                anchorEl={reportAnchorEl}
                open={Boolean(reportAnchorEl)}
                onClose={() => setReportAnchorEl(null)}
                MenuListProps={{
                  onMouseLeave: () => setReportAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setReportAnchorEl(null);
                    navigateToTab("Report", "Stock");
                  }}
                >
                  Stock
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setReportAnchorEl(null);
                    navigateToTab("Report", "Service");
                  }}
                >
                  Service
                </MenuItem>
              </Menu>
            </div> */}
            <div>
              <div
                onMouseEnter={(e) => {
                  setMasterAnchorEl(e.currentTarget);
                }}
                className={`flex-center cursor ${
                  activeTab === "Master"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Master <ArrowDropDownIcon fontSize="small" />
              </div>
              <Menu
                anchorEl={masterAnchorEl}
                open={Boolean(masterAnchorEl)}
                onClose={() => setMasterAnchorEl(null)}
                MenuListProps={{
                  onMouseLeave: () => setMasterAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setMasterAnchorEl(null);
                    setLocationInnerTab("Location");
                    navigateToTab("Master", "Location");
                  }}
                >
                  Location
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMasterAnchorEl(null);
                    navigateToTab("Master", "Supplier");
                  }}
                >
                  Supplier
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMasterAnchorEl(null);
                    setInventorySettingsInnerTab("Stock");
                    navigateToTab("Master", "Inventory Settings");
                  }}
                >
                  Inventory Settings
                </MenuItem>
              </Menu>
            </div>
            <div>
              <div
                onMouseEnter={(e) => {
                  setDownloadAnchorEl(e.currentTarget);
                }}
                className={`flex-center cursor ${
                  activeTab === "Download Reports"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Download Reports <ArrowDropDownIcon fontSize="small" />
              </div>
              <Menu
                anchorEl={downloadAnchorEl}
                open={Boolean(downloadAnchorEl)}
                onClose={() => setDownloadAnchorEl(null)}
                MenuListProps={{
                  onMouseLeave: () => setDownloadAnchorEl(null),
                }}
              >
                <MenuItem onClick={onOpenStockTransReport}>
                  Stock Transaction History
                </MenuItem>
              </Menu>
            </div>
          </div>

          {/* <Grid container spacing={2}>
            <Grid
              item
              xs={4}
              sm={4}
              md={2}
              lg={1.5}
              xl={1}
              onMouseEnter={(e) => {
                setStockAnchorEl(e.currentTarget);
              }}
              onMouseLeave={() => setStockAnchorEl(null)}
            >
              <Button
                text={
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {stockOption}
                    <ArrowDropDownIcon
                      style={{ color: "gray", fontSize: 20 }}
                    />
                  </span>
                }
                tabSelectdBtn={activeTab === "Stock"}
                textBtn={activeTab !== "Stock"}
                color={AppColors.grayBtn2}
              />
              <Menu
                anchorEl={stockAnchorEl}
                open={Boolean(stockAnchorEl)}
                onClose={() => setStockAnchorEl(null)}
                MenuListProps={{
                  onMouseEnter: () => {},
                  onMouseLeave: () => setStockAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setStockOption("Stock");
                    setActiveTab("Stock");
                    setStockAnchorEl(null);
                  }}
                >
                  Stock
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setStockOption("Dashboard");
                    setActiveTab("Stock");
                    setStockAnchorEl(null);
                  }}
                >
                  Dashboard
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setStockOption("Requests");
                    setActiveTab("Stock");
                    setStockAnchorEl(null);
                  }}
                >
                  Requests
                </MenuItem>
              </Menu>
            </Grid>
            <Grid
              item
              xs={4}
              sm={4}
              md={2}
              lg={1.5}
              xl={1}
              onMouseEnter={(e) => {
                setServiceAnchorEl(e.currentTarget);
              }}
              onMouseLeave={() => setServiceAnchorEl(null)}
            >
              <Button
                text={
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {serviceOption}
                    <ArrowDropDownIcon
                      style={{ color: "gray", fontSize: 20 }}
                    />
                  </span>
                }
                tabSelectdBtn={activeTab === "Service"}
                textBtn={activeTab !== "Service"}
                color={AppColors.grayBtn2}
              />
              <Menu
                anchorEl={serviceAnchorEl}
                open={Boolean(serviceAnchorEl)}
                onClose={() => setServiceAnchorEl(null)}
                MenuListProps={{
                  onMouseEnter: () => {},
                  onMouseLeave: () => setServiceAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setServiceOption("Service");
                    setActiveTab("Service");
                    setServiceAnchorEl(null);
                  }}
                >
                  Service
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setServiceOption("Dashboard");
                    setActiveTab("Service");
                    setServiceAnchorEl(null);
                  }}
                >
                  Dashboard
                </MenuItem>
              </Menu>
            </Grid>
            <Grid item xs={4} sm={4} md={2} lg={1.5} xl={1}>
              <div
                onMouseEnter={(e) => {
                  setMasterAnchorEl(e.currentTarget);
                }}
                className={`flex-center cursor ${
                  activeTab === "Master"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Master <ArrowDropDownIcon fontSize="small" />
              </div>
              <Menu
                anchorEl={masterAnchorEl}
                open={Boolean(masterAnchorEl)}
                onClose={() => setMasterAnchorEl(null)}
                MenuListProps={{
                  onMouseLeave: () => setMasterAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setMasterSubTab("Location");
                    setActiveTab("Master");
                    setMasterAnchorEl(null);
                  }}
                >
                  Location
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMasterSubTab("Supplier");
                    setActiveTab("Master");
                    setMasterAnchorEl(null);
                  }}
                >
                  Supplier
                </MenuItem>
              </Menu>
            </Grid>
            <Grid item xs={4} sm={4} md={2} lg={1.5} xl={1}>
              <Button
                text="Purchase History"
                tabSelectdBtn={activeTab === "Purchase History"}
                textBtn={activeTab !== "Purchase History"}
                color={AppColors.grayBtn2}
                onClick={() => setActiveTab("Purchase History")}
              />
            </Grid>
          </Grid> */}
        </Container>
        {/* <div className="pos-absolute r80 top65">
          <Typography
            className="fs14 txt-semi-bold orange-color cursor"
            onClick={handlePending}
          >
            Pending {pendingStocks?.totalRecords}
          </Typography>
        </div> */}
      </TopBar>
      {subTabsToShow?.includes(activeTab) ? (
        <Grid container spacing={0}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex-row light-blue-back3 pv5 mh30">
              {getTabBasedSubList(activeTab)?.map((st, i) => (
                <div className="pos-rel">
                  <Typography
                    key={`${st}_${i}`}
                    className={`ml20 newTab ph10 cursor ${activeSubTab === st ? "newTabSelected blue-color" : ""}`}
                    onClick={() => onSubTabChange(st)}
                  >
                    {st}
                  </Typography>
                  {st === "Pending to Add" && (
                    <div className="red-back2 fs10 br10 img-wh20 pos-absolute white-color rhtMin5 topMin5 flex-center">
                      {pendingStocks?.totalRecords}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Grid>
        </Grid>
      ) : null}

      <div
      // className="mt20"
      // style={{
      //   height: "calc(100vh - 130px)",
      //   overflowY: "auto",
      //   // paddingRight: "10px",
      // }}
      >
        {activeTab === "Dashboard" ? (
          <InventoryDashboard
            setActiveTab={setActiveTab}
            setActiveSubTab={setActiveSubTab}
          />
        ) : null}
        {activeTab === "Stock" ? (
          ["All Stock", "Low Stock", "Near Expiry", "Out of Stock"].includes(
            activeSubTab,
          ) ? (
            <Stock
              penTblVsble={penTblVsble}
              setPenTblVsble={setPenTblVsble}
              activeSubTab={activeSubTab}
            />
          ) : // activeSubTab === "Transaction History" ? (
          //   <TransactionHistory />
          // ) : activeSubTab === "Purchase History" ? (
          //   <PurchaseHistory />
          // ) :
          null
        ) : null}
        {activeTab === "Service" ? <Service /> : null}
        {activeTab === "Purchase" ? (
          activeSubTab === "Purchase Orders" ? (
            <PurchaseOrders />
          ) : activeSubTab === "Purchase Invoice" ? (
            <PurchaseInvoice />
          ) : (
            <StockPendingTable setPenTblVsble={setPenTblVsble} />
          )
        ) : null}
        {activeTab === "Adjustments & Transfers" ? (
          activeSubTab === "Transfers" ? (
            <StockRequest />
          ) : activeSubTab === "Adjustment History" ? (
            <AdjustmentHistory />
          ) : (
            <PendingReview />
          )
        ) : null}
        {activeTab === "Report" && (
          <Box
            sx={{
              height: "calc(100vh - 120px)",
              overflow: "auto",
              padding: "20px",
            }}
          >
            {activeSubTab === "Stock" ? (
              <Main2 />
            ) : activeSubTab === "Service" ? (
              <Main />
            ) : null}
          </Box>
        )}
        {activeTab === "Master" &&
          (activeSubTab === "Location" ? (
            <>
              <Grid container spacing={0}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="flex-row light-blue-back3 pv5 mh30">
                    {["Location", "Category"].map((st, i) => (
                      <div className="pos-rel" key={`${st}_${i}`}>
                        <Typography
                          className={`ml20 newTab ph10 cursor ${
                            locationInnerTab === st
                              ? "newTabSelected blue-color"
                              : ""
                          }`}
                          onClick={() => setLocationInnerTab(st)}
                        >
                          {st}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </Grid>
              </Grid>
              {locationInnerTab === "Location" ? (
                <Location />
              ) : (
                <LocationCategory />
              )}
            </>
          ) : activeSubTab === "Supplier" ? (
            <Supplier />
          ) : activeSubTab === "Inventory Settings" ? (
            <>
              <Grid container spacing={0}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="flex-row light-blue-back3 pv5 mh30">
                    {["Stock", "Services"].map((st, i) => (
                      <div className="pos-rel" key={`${st}_${i}`}>
                        <Typography
                          className={`ml20 newTab ph10 cursor ${
                            inventorySettingsInnerTab === st
                              ? "newTabSelected blue-color"
                              : ""
                          }`}
                          onClick={() => setInventorySettingsInnerTab(st)}
                        >
                          {st}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </Grid>
              </Grid>
              <InventorySettingsCategory
                categoryType={
                  inventorySettingsInnerTab === "Services"
                    ? "services"
                    : "stock"
                }
              />
            </>
          ) : null)}
        {/* {activeTab === "Service" &&
          (serviceOption === "Service" ? <Service /> : <Main />)}
        {activeTab === "Stock" &&
          (stockOption === "Stock" ? (
            <Stock penTblVsble={penTblVsble} setPenTblVsble={setPenTblVsble} />
          ) : stockOption === "Requests" ? (
            <StockRequest />
          ) : (
            <Main2 />
          ))}        
        {activeTab === "Purchase History" ? <PurchaseHistory /> : null} */}
      </div>

      <CustomModal
        open={stockTransModValues?.visible}
        onClose={onCloseStockTransMod}
        header="Stock Transaction History"
        modal={stockTransModValues?.valueEntered}
        rightModal={!stockTransModValues?.valueEntered}
        modalWidth={stockTransModValues?.valueEntered ? 95 : 30}
      >
        <div className="scroll-90vh">
          {stockTransModValues?.valueEntered ? (
            <TransactionHistory
              key={stockTransModValues?.stockName?.value}
              selectedStock={{
                stockId: stockTransModValues?.stockName?.value,
              }}
              initialStartDate={stockTransModValues?.startDate}
              initialEndDate={stockTransModValues?.endDate}
              initType={stockTransModValues?.type}
            />
          ) : (
            <div className="ph20">
              <div className="txt-semi-bold fs14 light-grey">
                Stock Name <span className="red-color">*</span>
              </div>
              <SearchableSelect
                list={allStocksList}
                value={stockTransModValues?.stockName ?? null}
                handleChange={(e, value) => {
                  onChangeStockTrans("stockName", value);
                }}
                maxFilteredOptions={100}
                error={!!stockTransModValues?.stockNameErr}
                helperText={stockTransModValues?.stockNameErr}
              />
              <div className="flex-row-between mt10">
                <div className="w48Per">
                  <div className="txt-semi-bold fs14 light-grey">
                    Start Date
                  </div>
                  <CustomTextField
                    value={stockTransModValues?.startDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) =>
                      onChangeStockTrans("startDate", e?.target?.value)
                    }
                  />
                </div>
                <div className="w48Per">
                  <div className="txt-semi-bold fs14 light-grey">End Date</div>
                  <CustomTextField
                    value={stockTransModValues?.endDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) =>
                      onChangeStockTrans("endDate", e?.target?.value)
                    }
                  />
                </div>
              </div>
              <div className="txt-semi-bold fs14 light-grey mt10">Type</div>
              <CustomSelect
                list={transactionTypeList}
                fullWidth
                name="type"
                handleChange={(e) => {
                  const { name, value } = e?.target ?? {};
                  onChangeStockTrans(name?.trim()?.replace(/,$/, ""), value);
                }}
                value={stockTransModValues?.type ?? "all"}
                label=""
                select
              />
              <div className="flex1-end mt20">
                <div>
                  <CustomButton
                    text="Generate"
                    onClick={onGenerateStockTrans}
                    smallBtn
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </CustomModal>
    </>
  );
};

export default Inventory;
